import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/Auth0Context'
import { useFeedSocket } from '@/contexts/FeedSocketContext'
import { feedService } from '@/services/feedService'
import { FeedSocketEvent } from '@/types/feed'
import type {
  IComment,
  IFeedCommentCreatedPayload,
  IFeedCommentDeletedPayload
} from '@/types/feed'

const PAGE_SIZE = 20

interface CommentThreadProps {
  postId: string
  /** Notifies the parent when the on-screen comment count changes. */
  onCountChange?: (count: number) => void
}

const CommentThread: React.FC<CommentThreadProps> = ({ postId, onCountChange }) => {
  const { user } = useAuth()
  const { subscribePost, unsubscribePost, on } = useFeedSocket()

  const [comments, setComments] = useState<IComment[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)

  const loadMore = useCallback(async (resetCursor?: string): Promise<void> => {
    setLoading(true)
    try {
      const page = await feedService.getComments(postId, { cursor: resetCursor, limit: PAGE_SIZE })
      setComments((prev) => (resetCursor === undefined ? page.items : [...prev, ...page.items]))
      setHasMore(page.hasMore)
      setCursor(page.nextCursor)
    } finally {
      setLoading(false)
    }
  }, [postId])

  useEffect(() => {
    void loadMore(undefined)
  }, [loadMore])

  useEffect(() => {
    subscribePost(postId)
    const offCreated = on<IFeedCommentCreatedPayload>(FeedSocketEvent.COMMENT_CREATED, (p) => {
      if (p.comment.postId !== postId) return
      setComments((prev) => (prev.find((c) => c.id === p.comment.id) ? prev : [...prev, p.comment]))
      onCountChange?.(p.commentCount)
    })
    const offDeleted = on<IFeedCommentDeletedPayload>(FeedSocketEvent.COMMENT_DELETED, (p) => {
      if (p.postId !== postId) return
      setComments((prev) => prev.filter((c) => c.id !== p.commentId))
      onCountChange?.(p.commentCount)
    })
    return () => {
      offCreated()
      offDeleted()
      unsubscribePost(postId)
    }
  }, [postId, on, subscribePost, unsubscribePost, onCountChange])

  const submitComment = async (): Promise<void> => {
    const text = draft.trim()
    if (!text || posting) return
    setPosting(true)
    try {
      const created = await feedService.createComment(postId, { content: text })
      // Optimistic: socket emit will also arrive; dedupe by id.
      setComments((prev) => (prev.find((c) => c.id === created.id) ? prev : [...prev, created]))
      setDraft('')
    } finally {
      setPosting(false)
    }
  }

  const remove = async (commentId: string): Promise<void> => {
    await feedService.deleteComment(commentId)
    setComments((prev) => prev.filter((c) => c.id !== commentId))
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {comments.map((c) => (
          <div key={c.id} className="rounded-md bg-gray-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <Link to={`/users/${c.authorId}`} className="text-sm font-medium text-gray-800 hover:underline">
                {c.author.firstName} {c.author.lastName}
              </Link>
              <span className="text-xs text-gray-400">
                {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{c.content}</p>
            {(c.authorId === user?.id) && (
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="mt-1 text-xs text-red-600 hover:underline"
              >
                Delete
              </button>
            )}
          </div>
        ))}
        {comments.length === 0 && !loading && (
          <p className="text-sm text-gray-400">No comments yet.</p>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => void loadMore(cursor)}
            className="text-sm text-primary-600 hover:underline disabled:opacity-50"
            disabled={loading}
          >
            Load more comments
          </button>
        )}
      </div>

      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Add a comment"
          className="flex-1 resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <Button
          type="button"
          size="sm"
          onClick={submitComment}
          isLoading={posting}
          disabled={draft.trim().length === 0}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default CommentThread
